// Arriving on the funnel: a FIRST VISIT, or a RETURN — and never once per page.
//
// ⚠ THIS WAS COUNTING PAGES, NOT VISITS. Every page of the funnel reported a `retour` as soon
// as a devis existed, so a single uninterrupted run showed four returns in the admin without
// the visitor ever leaving (reported 2026-10-15). A return is a visitor who went away and came
// BACK, which is a property of the session, not of the page.
//
// THE SESSION IS THE UNIT. `sessionStorage` lives exactly as long as a tab's visit: it survives
// navigation inside the funnel, and dies when the tab is closed. Finding it empty means this is
// a new visit; finding a devis already stored means the visitor had been here before.
//
// HOW THEY CAME BACK matters as much as the fact: someone who clicked a follow-up email is a
// campaign working, someone who typed the address again is a prospect thinking about it. The
// difference is readable, imperfectly, from the link they arrived by.

import { note } from '/shared/analytics/prospection.js';
import { readDevis } from '/shared/utils/core/devisRecord.js';
import { deviceLabel } from '/shared/analytics/device.js';

// Set for the whole visit the first time a page reports an arrival.
const SEEN_KEY = 'nb-visite';

/**
 * Report the arrival, once per visit.
 *
 * @param {string} lang the funnel's language on this page
 * @returns {void}
 */
export function arrivalGesture(lang) {
  if (alreadyCounted()) return;

  const known = Boolean(readDevis().id);
  note(known ? 'retour' : 'visite', {
    lang,
    landing: location.pathname,
    device: deviceLabel(),
    ...(known ? { via: howTheyCameBack() } : {}),
    // WHICH message brought them back, when the link says so. It is the same `emailId` the
    // send and its opens already carry (routes/api/mandrill-webhook.js), so a return lands on
    // the line of the very message that caused it instead of in a column marked "email".
    ...(emailBehind() ? { emailId: emailBehind() } : {}),
  });
}

// What a follow-up link may name itself with: `?courriel=<the message's id at Mandrill>`.
// Written down here because the sender is not us — the platform's follow-up cron composes
// those links (NB_Admin/docs/prospection-plateforme.md §2.3). Reading it costs nothing until
// the day they carry it, and costs a whole feature if nobody is ready to read it.
const EMAIL_PARAM = 'courriel';
// Mandrill's ids are short hex; anything else came from somewhere else and is not kept.
const EMAIL_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The message this arrival names, if it names one.
 *
 * @returns {string} the id, or '' — never what the URL said verbatim
 */
function emailBehind() {
  const given = new URLSearchParams(location.search).get(EMAIL_PARAM) ?? '';
  return EMAIL_ID.test(given) ? given : '';
}

/**
 * Has this visit already been reported? Marks it if not.
 *
 * Storage refused (private window, blocked cookies) answers "no" every time: the visit is then
 * counted once per page, which overstates returns — but a visitor nobody can follow is better
 * overstated than invisible, and the alternative is reporting nothing at all.
 *
 * @returns {boolean}
 */
function alreadyCounted() {
  try {
    if (sessionStorage.getItem(SEEN_KEY)) return true;
    sessionStorage.setItem(SEEN_KEY, String(Date.now()));
    return false;
  } catch {
    return false;
  }
}

/**
 * Which door they came back through.
 *
 * ⚠ READ FROM THE LINK, AND ONLY APPROXIMATELY. A follow-up email carries its own campaign
 * marks (`utm_medium=email`), which is the one signal that genuinely names an email. A referrer
 * tells where a click came from, but most mail clients send none at all — so its ABSENCE proves
 * nothing, and treating "no referrer" as "email" (as this did) called every direct return an
 * email click.
 *
 * @returns {string} 'courriel', 'lien' or 'direct'
 */
function howTheyCameBack() {
  const params = new URLSearchParams(location.search);
  const medium = (params.get('utm_medium') ?? '').toLowerCase();
  // A link that names its own message is the surest of the three: nothing else puts an id
  // there, and the gesture can then be read beside the send it came from.
  if (emailBehind()) return 'courriel';
  if (medium.includes('email') || medium.includes('courriel') || params.has('nb_relance')) {
    return 'courriel';
  }
  // A referrer from somewhere that is not us: a link they followed, wherever from.
  if (document.referrer && !document.referrer.includes(location.host)) return 'lien';
  return 'direct';
}
